/**
 * Sentry initialization for the Polygraf web client.
 *
 * Init is a no-op unless VITE_SENTRY_DSN is set at build/runtime. This keeps
 * local development quiet and lets us stage the rollout (deploy code first,
 * add DSN in Coolify second).
 *
 * Session Replay is configured with strict masking (maskAllText, maskAllInputs,
 * blockAllMedia) because the MUNI accessibility users include hearing- and
 * visually-impaired students — we must not accidentally record their content.
 */

import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from 'react-router-dom'

let initialized = false

/**
 * tRPC error codes that mean "the caller asked for something it may not
 * have" — an expired session, a space it cannot read, a session id that no
 * longer exists. They are 4xx equivalents: expected, handled by the UI, and
 * pure noise in an error tracker.
 *
 * `BAD_REQUEST` stays OUT of this set on purpose: on the web client a Zod
 * input-validation failure means the frontend sent a malformed payload, which
 * is a real bug and must reach Sentry.
 */
const CLIENT_ERROR_CODES: ReadonlySet<string> = new Set([
	'UNAUTHORIZED',
	'FORBIDDEN',
	'NOT_FOUND',
	'CONFLICT',
	'PRECONDITION_FAILED',
	'TOO_MANY_REQUESTS',
])

/**
 * Read the tRPC error code off a thrown value. `TRPCClientError` exposes the
 * server's error shape on `.data.code`; anything else returns `undefined`.
 * Structural check on purpose — the SDK config must not import tRPC.
 */
function trpcErrorCode(error: unknown): string | undefined {
	if (typeof error !== 'object' || error === null) return undefined
	const data = (error as { data?: unknown }).data
	if (typeof data !== 'object' || data === null) return undefined
	const code = (data as { code?: unknown }).code
	return typeof code === 'string' ? code : undefined
}

/**
 * Build the allowlist of origins that receive the `sentry-trace` /
 * `baggage` headers. Without the API origin, the browser span and the server
 * span stay two unrelated traces — polygraf.app and api.polygraf.app are
 * different origins, so the SDK default (same-origin only) never propagates.
 */
function tracePropagationTargets(): Array<string | RegExp> {
	const targets: Array<string | RegExp> = [/^\//]
	const apiUrl = import.meta.env.VITE_API_URL
	if (apiUrl) {
		try {
			targets.push(new URL(apiUrl).origin)
		} catch {
			// Malformed VITE_API_URL — same-origin propagation still applies.
		}
	}
	return targets
}

export function initSentry(): void {
	if (initialized) return

	const dsn = import.meta.env.VITE_SENTRY_DSN
	if (!dsn) {
		if (import.meta.env.DEV) {
			console.debug('[sentry] DSN not set, skipping init')
		}
		return
	}

	const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE
	const release = import.meta.env.VITE_SENTRY_RELEASE
	const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1)

	Sentry.init({
		dsn,
		environment,
		release,
		tracesSampleRate,
		tracePropagationTargets: tracePropagationTargets(),
		// The client polls /health on the API to drive the offline banner.
		// Those transactions would outnumber every real navigation.
		ignoreTransactions: [/\/health(?:\/|$)/],
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		integrations: [
			Sentry.reactRouterV7BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
			Sentry.replayIntegration({
				maskAllText: true,
				maskAllInputs: true,
				blockAllMedia: true,
			}),
		],
		sendDefaultPii: false,
		// Drop the benign, unhandled "Rejected" from the self-destroying PWA
		// cleanup service worker. `injectRegister: 'auto'` emits a `registerSW.js`
		// with no `.catch()` (see vite.config.ts), and the suicide SW's
		// `navigator.serviceWorker.register` can reject harmlessly on some mobile
		// browsers — it does not affect app functionality. Match on the stack
		// frame (registerSW.js / serviceWorker.register), never on the generic
		// "Rejected" message, so real errors are never suppressed.
		beforeSend(event, hint) {
			const frames =
				event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? []
			const fromServiceWorkerRegistration = frames.some(
				(frame) =>
					/registerSW\.js/.test(frame.filename ?? '') ||
					/serviceWorker\.register/.test(frame.function ?? ''),
			)
			if (fromServiceWorkerRegistration) return null

			// Drop expected 4xx tRPC failures — an expired session, a space the
			// caller may not read, a session id that no longer exists. The UI
			// already handles all of them; in Sentry they only bury real bugs.
			const code = trpcErrorCode(hint.originalException)
			if (code && CLIENT_ERROR_CODES.has(code)) return null

			return event
		},
	})

	initialized = true
}

export { Sentry }
